import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchListingBySlug, fetchListingById, fetchListings, type Listing } from "@/lib/listings";
import { listingPath } from "@/lib/slugify";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import LeadPreferencesForm from "@/components/LeadPreferencesForm";
import FinancingSimulator from "@/components/FinancingSimulator";
import { optimizedImage, optimizedSrcSet } from "@/lib/imageCdn";
import ScheduleVisitDialog, { ScheduleVisitForm } from "@/components/ScheduleVisitDialog";
import {
  Bed, BedDouble, Car, Maximize, MapPin, Phone, Bath, Building2,
  ChevronLeft, ChevronRight, X, Layers, DollarSign, Home, ArrowUp,
  Check, Sofa, Accessibility, Wrench, LayoutGrid, Heart, Play, Share2,
} from "lucide-react";
import { toast } from "sonner";

import { CATEGORIES } from "@/components/admin/AmenitiesSelector";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { useLanguage } from "@/contexts/LanguageContext";
import LandingHero from "@/components/listing/LandingHero";
import LandingCTA from "@/components/listing/LandingCTA";
import { hasHero, hasCta, type LandingConfig } from "@/lib/landingConfig";
import { buildListingGraph } from "@/lib/schemaOrg";

const formatPrice = (price: number, lang: string) =>
  new Intl.NumberFormat(lang === "en" ? "en-US" : "pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(price);

const categoryIcons: Record<string, any> = {
  condominium_amenities: Building2,
  unit_amenities: Home,
  wellness_features: Heart,
  furniture_features: Sofa,
  accessibility_features: Accessibility,
  appliance_features: Wrench,
  room_features: LayoutGrid,
};

function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
  return match?.[1] || null;
}


const ListingDetail = () => {
  const { slug } = useParams<{ slug: string; neighborhoodSlug: string }>();
  const [searchParams] = useSearchParams();
  const consultantMode = searchParams.get("consultor") === "1";
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [related, setRelated] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImg = () => setLightboxIndex((i) => (i !== null && listing?.images ? (i - 1 + listing.images.length) % listing.images.length : null));
  const nextImg = () => setLightboxIndex((i) => (i !== null && listing?.images ? (i + 1) % listing.images.length : null));

  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    const load = async () => {
      try {
        let data: Listing;
        try { data = await fetchListingBySlug(slug); } catch { data = await fetchListingById(slug); }
        setListing(data);
        // SEO overrides configured per-listing (fall back to computed values when empty)
        const seo = data as any;
        const defaultTitle = `${data.title} - ${data.neighborhood} | Topclass Imóveis`;
        const pageTitle = seo.meta_title || defaultTitle;
        document.title = pageTitle;

        // Per-route canonical: use override if provided, else the listing's URL on the production domain
        const canonicalUrl = seo.canonical_url || `https://topclassimoveis.com${listingPath(data)}`;
        let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!link) {
          link = document.createElement("link");
          link.setAttribute("rel", "canonical");
          document.head.appendChild(link);
        }
        link.setAttribute("href", canonicalUrl);

        // Per-route Open Graph tags (for crawlers that execute JS / re-fetches)
        const setMeta = (key: "property" | "name", val: string, content: string) => {
          let m = document.querySelector<HTMLMetaElement>(`meta[${key}="${val}"]`);
          if (!m) { m = document.createElement("meta"); m.setAttribute(key, val); document.head.appendChild(m); }
          m.setAttribute("content", content);
        };
        const ogImg = seo.og_image || data.images?.[0] || "https://topclassimoveis.com/og-default.jpg";
        const ogTitle = seo.og_title || pageTitle;
        const computedDesc = (data.description || "").slice(0, 200) || `Imóvel em ${data.neighborhood} - Topclass Imóveis`;
        const ogDesc = seo.og_description || seo.meta_description || computedDesc;
        setMeta("property", "og:title", ogTitle);
        setMeta("property", "og:description", ogDesc);
        setMeta("property", "og:image", ogImg);
        setMeta("property", "og:url", canonicalUrl);
        setMeta("property", "og:type", "article");
        setMeta("name", "twitter:title", ogTitle);
        setMeta("name", "twitter:description", ogDesc);
        setMeta("name", "twitter:image", ogImg);

        // Robots directive (only set when the listing supplies one)
        if (seo.robots) setMeta("name", "robots", seo.robots);

        // Google tag (gtag.js) only for the specific Ipanema listing page
        const targetPath = "/ipanema/apartamento-a-venda-em-ipanema-com-vista-para-a-lagoa-rodrigo-de-freitas-100m-2-quartos-mobiliado-e-1-vaga";
        if (window.location.pathname === targetPath) {
          const gtagId = "G-M9YEWGDLY0";
          if (!document.getElementById(`ga-script-${gtagId}`)) {
            const asyncScript = document.createElement("script");
            asyncScript.id = `ga-script-${gtagId}`;
            asyncScript.async = true;
            asyncScript.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
            document.head.appendChild(asyncScript);

            const inlineScript = document.createElement("script");
            inlineScript.id = `ga-inline-${gtagId}`;
            inlineScript.innerHTML = `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gtagId}');
            `;
            document.head.appendChild(inlineScript);
          }
        }


        // Per-page <meta name="description"> and <meta name="keywords">
        const parts: string[] = [];
        if (data.bedrooms) {
          const suites = (data as any).suites ? ` (${(data as any).suites} suíte${(data as any).suites > 1 ? "s" : ""})` : "";
          parts.push(`${data.bedrooms} quartos${suites}`);
        }
        if ((data as any).bathrooms) parts.push(`${(data as any).bathrooms} banheiros`);
        if ((data as any).area_m2 || (data as any).area) parts.push(`~${(data as any).area_m2 || (data as any).area} m² privativos`);
        if ((data as any).garage_spots || (data as any).parking_spots) {
          const g = (data as any).garage_spots || (data as any).parking_spots;
          parts.push(`${g} vaga${g > 1 ? "s" : ""}`);
        }
        const specs = parts.join(" · ");
        const propType = (data as any).property_type ? String((data as any).property_type).replace(/^./, (c) => c.toUpperCase()) : "Imóvel";
        const computedMetaDesc = `${propType} em ${data.neighborhood} Rio de Janeiro (RJ)${specs ? " • " + specs : ""}`.slice(0, 158);
        const metaDesc = seo.meta_description || computedMetaDesc;
        setMeta("name", "description", metaDesc);
        const kwPrimary = (seo.keywords_primary || []) as string[];
        const kwSecondary = (seo.keywords_secondary || []) as string[];
        const kwParts = kwPrimary.length || kwSecondary.length
          ? [...kwPrimary, ...kwSecondary]
          : [data.title, data.neighborhood, `${propType} ${data.neighborhood}`, "Topclass Imóveis",
             ...(data.bedrooms ? [`${propType} ${data.bedrooms} quartos ${data.neighborhood}`] : [])];
        setMeta("name", "keywords", kwParts.join(", ").slice(0, 250));

        // Google Ads tag page-specific config for this listing
        if (typeof window !== "undefined" && (window as any).gtag) {
          (window as any).gtag("config", "AW-8733287649", {
            page_title: ogTitle,
            page_path: listingPath(data),
          });
          (window as any).gtag("event", "conversion", {
            send_to: "AW-8733287649/7558379247",
            value: 1.0,
            currency: "BRL",
          });
        }

        const manualIds = ((data as any).similar_listing_ids || []) as string[];
        if (manualIds.length > 0) {
          const results = await Promise.all(
            manualIds.map((id) => fetchListingById(id).catch(() => null))
          );
          setRelated(results.filter((r): r is Listing => !!r && r.is_active !== false));
        } else {
          const all = await fetchListings({ neighborhood: data.neighborhood });
          setRelated((all || []).filter((l) => l.id !== data.id).slice(0, 3));
        }
      } catch { setListing(null); } finally { setLoading(false); }
    };
    load();
    window.scrollTo(0, 0);

    // Restore sitewide canonical when leaving the page
    return () => {
      const link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (link) link.setAttribute("href", "https://topclassimoveis.com/");
    };
  }, [slug]);


  useEffect(() => {
    if (!listing) return;
    const l: any = listing;
    const url = window.location.pathname;
    const graph: any = buildListingGraph(l, url);

    // Enriquecimento semântico para IA/AEO (Fase 5)
    const aiSeo = (l.ai_seo as any) || null;
    const residence = graph["@graph"].find((n: any) =>
      Array.isArray(n["@type"]) ? n["@type"].includes("Residence") : n["@type"] === "Residence",
    );
    if (residence && aiSeo) {
      if (Array.isArray(aiSeo.semantic_entities) && aiSeo.semantic_entities.length) {
        residence.about = aiSeo.semantic_entities.map((name: string) => ({ "@type": "Thing", name }));
      }
      if (Array.isArray(aiSeo.related_keywords) && aiSeo.related_keywords.length) {
        residence.keywords = aiSeo.related_keywords.join(", ");
      }
      if (aiSeo.target_persona) {
        residence.audience = { "@type": "PeopleAudience", audienceType: aiSeo.target_persona };
      }
      if (aiSeo.ai_summary) residence.abstract = aiSeo.ai_summary;
    }
    if (residence && (l.broker_name || l.broker_creci)) {
      residence.broker = {
        "@type": "RealEstateAgent",
        name: l.broker_name || "TopClass Imóveis",
        image: l.broker_photo || undefined,
        telephone: l.broker_phone || "+5521973548387",
        identifier: l.broker_creci ? `CRECI ${l.broker_creci}` : undefined,
      };
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-listing";
    document.getElementById("ld-listing")?.remove();
    script.text = JSON.stringify(graph);
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-listing")?.remove();
    };
  }, [listing, language, t]);


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24">
          <div className="animate-pulse space-y-6">
            <div className="h-[60vh] bg-muted rounded-lg" />
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-foreground mb-4">{t("listings.notFound")}</h1>
          <Button variant="gold" asChild><Link to="/#imoveis" title="Ver todos os imóveis disponíveis">{t("listings.backToListings")}</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const l = listing as any;
  const propertyTypeLabel = t(`type.${listing.property_type}`) || listing.property_type;

  const whatsappMsg = t("detail.whatsappMsg")
    .replace("{title}", listing.title)
    .replace("{price}", formatPrice(listing.price, language));
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;
  const mapQuery = encodeURIComponent(`${listing.address || listing.title}, ${listing.neighborhood}, Rio de Janeiro`);
  const images = listing.images || [];
  const heroImage = images[0] || "/placeholder.svg";
  const watermarkEnabled = !!(l.watermark_enabled && l.watermark_logo);
  const watermarkUrl: string | null = watermarkEnabled ? l.watermark_logo : null;
  const Watermark = ({ size = "md" }: { size?: "sm" | "md" | "lg" }) =>
    watermarkUrl ? (
      <img
        src={watermarkUrl}
        alt=""
        aria-hidden="true"
        className={`pointer-events-none absolute bottom-3 right-3 opacity-70 drop-shadow-lg ${
          size === "lg" ? "w-32 md:w-44" : size === "sm" ? "w-14" : "w-24 md:w-28"
        }`}
      />
    ) : null;

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.phone.trim()) return;
    const { error } = await submitCrmLead({
      name: contactForm.name,
      phone: contactForm.phone,
      email: contactForm.email,
      propertyName: `Agende sua visita — ${listing.title}`,
      neighborhood: listing.neighborhood,
      temperature: "quente",
      notes: [
        `Valor: ${formatPrice(listing.price, language)}`,
        contactForm.message.trim() && `Mensagem: ${contactForm.message.trim()}`,
      ].filter(Boolean) as string[],
    });
    if (error) {
      toast.error("Não foi possível enviar. Tente novamente ou fale pelo WhatsApp.");
      return;
    }
    setContactSubmitted(true);
    navigate(THANK_YOU.imoveis);
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) return;
    const { error } = await submitCrmLead({
      name: leadForm.name,
      phone: leadForm.phone,
      email: leadForm.email,
      propertyName: `Busca outras opções de ${propertyTypeLabel.toLowerCase()} — ${listing.title}`,
      neighborhood: listing.neighborhood,
      notes: ["Cliente não encontrou o imóvel ideal e busca outras opções."],
    });
    if (error) {
      toast.error("Não foi possível enviar. Tente novamente ou fale pelo WhatsApp.");
      return;
    }
    setLeadSubmitted(true);
    navigate(THANK_YOU.imoveis);
  };

  const amenityCategories = (Object.keys(CATEGORIES) as Array<keyof typeof CATEGORIES>).filter(
    (cat) => (l[cat] as string[] | undefined)?.length > 0
  );

  const videoUrl: string | null = l.video_url || null;
  const youtubeId = videoUrl ? getYouTubeId(videoUrl) : null;

  const bedroomsSub = listing.suites
    ? `${t("detail.including")} ${listing.suites} ${listing.suites > 1 ? t("detail.suites") : t("detail.suite")}`
    : t("detail.dormitories");
  const bathroomCount = listing.bathrooms || 1;
  const garageCount = listing.garage_spots || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ── Hero Section ── Custom landing hero when configured, else default full-bleed hero ── */}
      {hasHero((listing as any).landing_config as LandingConfig) ? (
        <LandingHero
          hero={((listing as any).landing_config as LandingConfig).hero!}
          title={listing.title}
          address={listing.address}
          neighborhood={listing.neighborhood}
          fallbackImage={heroImage}
          whatsappUrl={whatsappUrl}
        />
      ) : (
      <section className="relative bg-background md:pt-0 pt-20">
        {/* Mobile: imagem contida (comportamento original) */}
        <div className="md:hidden container mx-auto px-4">
          <div className="relative w-full flex justify-center aspect-[4/3] bg-muted overflow-hidden rounded-lg">
            <img
              src={optimizedImage(heroImage, 900)}
              srcSet={optimizedSrcSet(heroImage, [640, 900, 1200])}
              sizes="100vw"
              width={900}
              height={675}
              alt={`${listing.title} - ${propertyTypeLabel} em ${listing.neighborhood}, Rio de Janeiro`}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
        {/* Desktop: imagem preenchendo toda a tela com texto sobreposto à esquerda (igual HeroSection) */}
        <div className="hidden md:flex relative min-h-[90vh] items-center justify-start overflow-hidden">
          <img
            src={optimizedImage(heroImage, 1600)}
            srcSet={optimizedSrcSet(heroImage, [1024, 1600, 1920])}
            sizes="100vw"
            alt={`${listing.title} - ${propertyTypeLabel} em ${listing.neighborhood}, Rio de Janeiro`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/50 to-transparent" />
          <div className="relative z-10 container mx-auto px-4 py-20">
            <div className="max-w-2xl animate-fade-in-up">
              <span className="inline-block rounded bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground mb-6 font-body">
                {propertyTypeLabel} · {listing.neighborhood}
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
                {listing.title}
              </h1>
              {(listing.address || listing.neighborhood) && (
                <p className="flex items-start gap-2 text-white/90 font-body text-lg mb-6 drop-shadow">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />
                  <span>{listing.address ? `${listing.address} · ` : ""}{listing.neighborhood}, RJ</span>
                </p>
              )}
              <p className="font-display text-3xl lg:text-4xl font-bold text-accent drop-shadow-lg mb-8">
                {formatPrice(listing.price, language)}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="gold" size="lg" className="text-lg px-8"><Phone className="w-5 h-5" />{t("detail.scheduleWhatsApp")}</Button>
                </a>
                <ScheduleVisitDialog
                  propertyName={listing.title}
                  neighborhood={listing.neighborhood}
                  variant="heroOutline"
                  size="lg"
                  className="text-lg px-8"
                  label={t("detail.requestInfo")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Custom CTA block (renders only when configured) ── */}
      {hasCta((listing as any).landing_config as LandingConfig) && (
        <LandingCTA
          cta={((listing as any).landing_config as LandingConfig).cta!}
          whatsappUrl={whatsappUrl}
          whatsappNumber={WHATSAPP_NUMBER}
          agendarHref="#agendar"
        />
      )}

      {/* Título, descrição e CTAs abaixo da foto (título/preço escondidos no desktop pois já estão sobrepostos) */}
      <section className="bg-background pt-4 pb-10 border-b border-border">
        <div className="container mx-auto px-4">
          <h1 className="md:hidden font-display text-4xl font-bold text-foreground leading-tight max-w-3xl mb-3">{listing.title}</h1>
          {(listing.address || listing.neighborhood) && (
            <p className="md:hidden flex items-start gap-2 text-foreground/70 font-body text-base mb-4">
              <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />
              <span>{listing.address ? `${listing.address} · ` : ""}{listing.neighborhood}, RJ</span>
            </p>
          )}
          <p className="text-foreground/80 font-body text-lg md:text-xl max-w-2xl mb-2 md:mt-2">
            {listing.bedrooms} {t("detail.bedrooms").toLowerCase()}{listing.suites ? ` · ${listing.suites} ${t("detail.suites")}` : ""}{listing.bathrooms ? ` · ${listing.bathrooms} ${t("detail.bathrooms").toLowerCase()}` : ""}{` · ${listing.area_m2}m²`}{listing.garage_spots ? ` · ${listing.garage_spots} ${listing.garage_spots > 1 ? t("detail.spots").toLowerCase() : t("detail.spot").toLowerCase()}` : ""}
          </p>
          <div className="md:hidden flex flex-wrap items-baseline gap-4 mb-6">
            <p className="font-display text-3xl font-bold text-accent">{formatPrice(listing.price, language)}</p>
            {listing.price_per_m2 && <p className="font-body text-lg text-muted-foreground">({formatPrice(listing.price_per_m2, language)}/m²)</p>}
          </div>
          {listing.price_per_m2 && (
            <p className="hidden md:block font-body text-lg text-muted-foreground mb-6">({formatPrice(listing.price_per_m2, language)}/m²)</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {listing.listing_code && <span className="inline-block bg-muted text-foreground text-sm font-semibold font-body px-4 py-1.5 rounded-full">{t("detail.code")}: {listing.listing_code}</span>}
            {l.is_furnished === true && <span className="inline-block bg-green-600 text-white text-sm font-semibold font-body px-4 py-1.5 rounded-full">{t("detail.furnished")}</span>}
            {l.near_metro === true && <span className="inline-block bg-muted text-foreground text-sm font-semibold font-body px-4 py-1.5 rounded-full">{t("detail.nearMetro")}</span>}
            {l.accepts_airbnb === true && <span className="inline-block bg-muted text-foreground text-sm font-semibold font-body px-4 py-1.5 rounded-full">{t("detail.acceptsAirbnb") || "Aceita Airbnb"}</span>}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="gold" size="lg" className="text-lg px-8 py-4"><Phone className="w-5 h-5" />{t("detail.scheduleWhatsApp")}</Button>
            </a>
            <Button
              variant="secondary"
              size="lg"
              className="text-lg px-8 py-4 text-black"
              onClick={async () => {
                const shareUrl = `${window.location.origin}${window.location.pathname}`;
                const shareData = { title: listing.title, text: listing.title, url: shareUrl };
                try {
                  if (navigator.share) {
                    await navigator.share(shareData);
                    return;
                  }
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success("Link copiado! Agora é só enviar para o seu amigo(a).");
                } catch {
                  /* usuário cancelou */
                }
              }}
            >
              <Share2 className="w-5 h-5" />Envie este imóvel para um Amigo(a)
            </Button>
          </div>
        </div>
      </section>

      {/* ── Details & Price Card ── */}
      <section className="py-12 bg-card" id="detalhes">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-display text-3xl font-bold text-foreground mb-8">{t("detail.roomsTitle")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { icon: BedDouble, label: `${listing.bedrooms} ${t("detail.bedrooms")}`, sub: bedroomsSub },
                  { icon: Bath, label: `${bathroomCount} ${bathroomCount > 1 ? t("detail.bathrooms") : t("detail.bathroom")}`, sub: t("detail.socialSuite") },
                  { icon: Car, label: `${garageCount} ${garageCount !== 1 ? t("detail.spots") : t("detail.spot")}`, sub: t("detail.garage") },
                  { icon: Maximize, label: `${listing.area_m2}m²`, sub: t("detail.totalArea") },
                  { icon: ArrowUp, label: listing.floor || "—", sub: t("detail.floor") },
                  { icon: Home, label: propertyTypeLabel, sub: t("detail.propertyType") },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-muted">
                    <item.icon className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-body font-semibold text-foreground">{item.label}</p>
                      <p className="font-body text-sm text-muted-foreground">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-secondary text-secondary-foreground rounded-xl p-8 flex flex-col justify-between shadow-lg">
              <div>
                <p className="font-body text-secondary-foreground/70 text-sm uppercase tracking-wider mb-1">{t("detail.salePrice")}</p>
                <p className="font-display text-4xl font-bold text-accent mb-2">{formatPrice(listing.price, language)}</p>
                {listing.price_per_m2 && <p className="font-body text-accent/80 text-sm mb-6"><DollarSign className="w-4 h-4 inline" /> {formatPrice(listing.price_per_m2, language)}/m²</p>}
                {listing.listing_code && <p className="font-body text-secondary-foreground/50 text-xs mb-4">{t("detail.code")}: {listing.listing_code}</p>}
                {(listing.condominium_fee || listing.iptu) && (
                  <div className="space-y-3 border-t border-secondary-foreground/10 pt-4">
                    {listing.condominium_fee && <div className="flex justify-between font-body"><span className="text-secondary-foreground/70">{t("detail.condo")}</span><span className="font-semibold">{formatPrice(listing.condominium_fee, language)}/{t("detail.month")}</span></div>}
                    {listing.iptu && <div className="flex justify-between font-body"><span className="text-secondary-foreground/70">IPTU</span><span className="font-semibold">{formatPrice(listing.iptu, language)}/{t("detail.year")}</span></div>}
                  </div>
                )}
              </div>
              <div className="mt-8 space-y-3">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"><Button variant="gold" className="w-full"><Phone className="w-5 h-5" />{t("detail.whatsappChat")}</Button></a>
                <ScheduleVisitDialog
                  propertyName={listing.title}
                  neighborhood={listing.neighborhood}
                  variant="outline"
                  size="default"
                  className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  label={t("detail.moreInfo")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Lead qualification form (same as welcome pop-up) ── */}
      <section className="py-14 bg-muted" id="perfil">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground text-center uppercase tracking-tight">
              Seja bem-vindo a TopClass Imóveis Premium!
            </h2>
            <p className="font-display text-lg sm:text-xl md:text-2xl font-semibold text-foreground text-center mt-3">
              Gostaria de receber informações exclusivas, vídeos e outros imóveis como este?
            </p>
            <div className="font-body text-sm sm:text-base text-muted-foreground leading-relaxed space-y-3 mt-4">
              <p>
                Preencha este formulário e te enviaremos imóveis de acordo com suas preferências,
                necessidades e faixa de investimento.
              </p>
              <p className="font-bold text-foreground">
                Fique tranquilo, não fazemos Spam e respeitamos a sua privacidade.
              </p>
            </div>
            <LeadPreferencesForm
              compact
              thankYouPath={THANK_YOU.imoveis}
              source={typeof window !== "undefined" ? window.location.href : `/${listing.slug}`}
              defaultNeighborhood={listing.neighborhood}
            />

          </div>
        </div>
      </section>



      {/* ── Gallery ── */}
      {images.length > 1 && (
        <section className="py-16 bg-muted" id="fotos">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">{t("detail.gallery")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => openLightbox(i)} className={`overflow-hidden rounded-lg cursor-pointer group relative ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                  <img src={optimizedImage(img, 600)} srcSet={optimizedSrcSet(img, [400, 600, 900])} sizes="(min-width: 768px) 25vw, 50vw" alt={`${listing.title} - foto ${i + 1}`} className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  <Watermark size="sm" />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
                </button>
              ))}
            </div>
          </div>
          {lightboxIndex !== null && (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
              <button aria-label="Fechar galeria" onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
              <button aria-label="Imagem anterior" onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-4 text-white/80 hover:text-white"><ChevronLeft className="w-10 h-10" /></button>
              <button aria-label="Próxima imagem" onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-4 text-white/80 hover:text-white"><ChevronRight className="w-10 h-10" /></button>
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <img src={optimizedImage(images[lightboxIndex], 1600)} alt={`${listing.title} - foto ${lightboxIndex + 1}`} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" />
                {watermarkUrl && <img src={watermarkUrl} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 w-32 md:w-44 opacity-70 drop-shadow-lg" />}
              </div>
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm font-body">{lightboxIndex + 1} / {images.length}</span>
            </div>
          )}
        </section>
      )}

      {/* ── CTA após a galeria de fotos ── */}
      {images.length > 1 && (
        <section className="py-12 bg-background border-y border-border">
          <div className="container mx-auto px-4 text-center">
            <ScheduleVisitDialog
              propertyName={listing.title}
              neighborhood={listing.neighborhood}
              label="Quero conhecer este imóvel"
              className="text-lg px-10 py-6"
            />
          </div>
        </section>
      )}

      {/* ── Video ── */}
      {videoUrl && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              <Play className="w-8 h-8 inline mr-2" />{t("detail.video")}
            </h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-lg aspect-video">
              {youtubeId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  title={`${t("detail.video")} - ${listing.title}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              ) : (
                <video src={videoUrl} controls className="w-full h-full" />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Resumo para IA (AEO) — visível para crawlers de IA ── */}
      {(l.ai_seo as any)?.ai_summary && (
        <section className="py-10 bg-primary/5 border-y border-primary/20" id="resumo-ia" aria-label="Resumo do imóvel">
          <div className="container mx-auto px-4 max-w-4xl">
            <p
              className="font-body text-foreground text-lg md:text-xl leading-relaxed text-center italic"
              itemProp="abstract"
            >
              {(l.ai_seo as any).ai_summary}
            </p>
          </div>
        </section>
      )}

      {/* ── Description ── */}
      {listing.description && (
        <section className="py-16 bg-background" id="descricao">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">{t("detail.about")}</h2>
            <div className="space-y-6 font-body text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{listing.description}</div>
          </div>
        </section>
      )}

      {/* ── Fase 2 · Conteúdo rico ── */}
      {(l.description_full || l.why_special || l.about_location || l.buyer_profile || l.differentials) && (
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4 max-w-4xl space-y-10">
            {l.description_full && (
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">Sobre este imóvel</h2>
                <div className="font-body text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{l.description_full}</div>
              </div>
            )}
            {l.why_special && (
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">Por que este imóvel é especial</h2>
                <div className="font-body text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{l.why_special}</div>
              </div>
            )}
            {l.about_location && (
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">Sobre a localização</h2>
                <div className="font-body text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{l.about_location}</div>
              </div>
            )}
            {l.differentials && (
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">Diferenciais</h2>
                <div className="font-body text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{l.differentials}</div>
              </div>
            )}
            {l.buyer_profile && (
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">Perfil do comprador</h2>
                <div className="font-body text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{l.buyer_profile}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Fase 2 · Avaliação do imóvel ── */}
      {l.ratings && Object.values(l.ratings).some((v: any) => typeof v === "number" && v > 0) && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">Avaliação do imóvel</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: "view", label: "Vista" },
                { key: "location", label: "Localização" },
                { key: "privacy", label: "Privacidade" },
                { key: "exclusivity", label: "Exclusividade" },
                { key: "liquidity", label: "Liquidez" },
              ].map(({ key, label }) => {
                const v = Number(l.ratings?.[key] || 0);
                if (!v) return null;
                return (
                  <div key={key} className="bg-card border border-border rounded-xl p-4 text-center shadow-sm">
                    <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
                    <p className="font-display text-2xl font-bold text-accent mb-1">{v.toFixed(1)}</p>
                    <div className="text-accent text-sm" aria-label={`${v} de 5`}>
                      {"★".repeat(Math.round(v))}<span className="text-muted-foreground/40">{"★".repeat(5 - Math.round(v))}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Fase 2 · Galeria por ambiente ── */}
      {Array.isArray(l.photo_sections) && l.photo_sections.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">Galeria por ambiente</h2>
            <div className="space-y-10">
              {l.photo_sections.map((sec: any) => (
                sec?.images?.length ? (
                  <div key={sec.key}>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-foreground mb-4">{sec.title}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {sec.images.map((img: string, i: number) => (
                        <div key={i} className="relative overflow-hidden rounded-lg aspect-square group">
                          <img src={optimizedImage(img, 700)} alt={`${sec.title} - foto ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <Watermark size="sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA após a galeria por ambiente ── */}
      {Array.isArray(l.photo_sections) && l.photo_sections.length > 0 && (
        <section className="py-12 bg-background border-y border-border">
          <div className="container mx-auto px-4 text-center">
            <ScheduleVisitDialog
              propertyName={listing.title}
              neighborhood={listing.neighborhood}
              label="Quero conhecer este imóvel"
              className="text-lg px-10 py-6"
            />
          </div>
        </section>
      )}

      {/* ── Fase 2 · Mídias extras (Drone / Tour Virtual / Planta / PDF) ── */}
      {(l.drone_url || l.virtual_tour_url || l.floorplan_url || l.pdf_url) && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">Mídias adicionais</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {l.drone_url && (() => {
                const dId = getYouTubeId(l.drone_url);
                return (
                  <div className="rounded-xl overflow-hidden border border-border shadow-lg aspect-video">
                    {dId ? (
                      <iframe src={`https://www.youtube.com/embed/${dId}`} title="Vídeo drone" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" />
                    ) : (
                      <video src={l.drone_url} controls className="w-full h-full" />
                    )}
                  </div>
                );
              })()}
              {l.virtual_tour_url && (
                <div className="rounded-xl overflow-hidden border border-border shadow-lg aspect-video">
                  <iframe src={l.virtual_tour_url} title="Tour virtual" className="w-full h-full" allow="fullscreen; vr; xr-spatial-tracking" allowFullScreen loading="lazy" />
                </div>
              )}
              {l.floorplan_url && (
                <a href={l.floorplan_url} target="_blank" rel="noopener noreferrer" className="rounded-xl overflow-hidden border border-border shadow-lg block">
                  <img src={optimizedImage(l.floorplan_url, 1200)} alt="Planta baixa" className="w-full h-auto object-contain bg-card" loading="lazy" />
                  <p className="p-3 text-center font-body text-sm text-muted-foreground">Ver planta baixa em tela cheia</p>
                </a>
              )}
              {l.pdf_url && (
                <a href={l.pdf_url} target="_blank" rel="noopener noreferrer" className="rounded-xl border border-border shadow-lg p-8 flex flex-col items-center justify-center text-center bg-card hover:bg-muted transition-colors">
                  <div className="text-5xl mb-3">📄</div>
                  <p className="font-display text-lg font-bold text-foreground mb-1">Apresentação em PDF</p>
                  <p className="font-body text-sm text-muted-foreground">Clique para abrir</p>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Fase 5 · Explore também (links internos semânticos) ── */}
      {Array.isArray((l as any).ai_seo?.internal_links) && (l as any).ai_seo.internal_links.length > 0 && (
        <section className="py-12 bg-background border-t border-border">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-6 text-center">
              Explore também
            </h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {(l as any).ai_seo.internal_links
                .filter((link: any) => link?.label && link?.url)
                .map((link: any, i: number) => (
                  <a
                    key={i}
                    href={link.url}
                    className="inline-flex items-center px-4 py-2 rounded-full border border-border bg-card text-sm text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
            </div>
          </div>
        </section>
      )}


      {/* ── Fase 3 · Lifestyle ── */}
      {Array.isArray(l.lifestyle) && l.lifestyle.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">
              Viva no imóvel
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {l.lifestyle.map((b: any, i: number) => (
                <div key={i} className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="text-4xl mb-3">{b.icon}</div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">{b.title}</h3>
                  {b.description && <p className="font-body text-muted-foreground leading-relaxed">{b.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Fase 3 · Comparativo automático ── */}
      {l.comparison_config?.enabled && related.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3 text-center">
              Comparativo
            </h2>
            <p className="font-body text-muted-foreground text-center mb-10">
              Veja como este imóvel se compara aos semelhantes.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-lg">
              <table className="w-full text-left">
                <thead className="bg-primary text-primary-foreground">
                  <tr>
                    <th className="p-3 font-display text-sm">Critério</th>
                    <th className="p-3 font-display text-sm bg-accent text-accent-foreground">
                      Este imóvel
                      {l.comparison_config?.highlight && (
                        <span className="block text-[10px] font-body font-normal mt-0.5 opacity-90">
                          {l.comparison_config.highlight}
                        </span>
                      )}
                    </th>
                    {related.map((r) => (
                      <th key={r.id} className="p-3 font-display text-sm min-w-[140px]">{r.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-body text-sm">
                  {[
                    { label: "Preço", get: (x: any) => formatPrice(x.price, language) },
                    { label: "Área", get: (x: any) => `${x.area_m2} m²` },
                    { label: "Quartos", get: (x: any) => x.bedrooms },
                    { label: "Suítes", get: (x: any) => x.suites || 0 },
                    { label: "Banheiros", get: (x: any) => x.bathrooms || 1 },
                    { label: "Vagas", get: (x: any) => x.garage_spots || 0 },
                    { label: "Bairro", get: (x: any) => x.neighborhood },
                    { label: "R$/m²", get: (x: any) => x.area_m2 ? formatPrice(Math.round(x.price / x.area_m2), language) : "—" },
                  ].map((row) => (
                    <tr key={row.label} className="border-t border-border">
                      <td className="p-3 font-semibold text-foreground">{row.label}</td>
                      <td className="p-3 bg-accent/10 text-foreground font-semibold">{row.get(listing)}</td>
                      {related.map((r) => (
                        <td key={r.id} className="p-3 text-muted-foreground">{row.get(r)}</td>
                      ))}
                    </tr>
                  ))}
                  {(l.comparison_config?.extras || []).map((x: any, i: number) => (
                    x.label ? (
                      <tr key={`x-${i}`} className="border-t border-border">
                        <td className="p-3 font-semibold text-foreground">{x.label}</td>
                        <td className="p-3 bg-accent/10 text-foreground font-semibold">{x.value || "—"}</td>
                        {related.map((r) => (
                          <td key={r.id} className="p-3 text-muted-foreground">—</td>
                        ))}
                      </tr>
                    ) : null
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {l.institutional_text && (
        <section className="py-10 bg-secondary">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <p className="font-body text-secondary-foreground/80 text-base italic whitespace-pre-line">{l.institutional_text}</p>
          </div>
        </section>
      )}






      {/* ── Features / Highlights ── */}
      {listing.features && listing.features.length > 0 && (
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">{t("detail.highlights")}</h2>
            <p className="font-body text-muted-foreground text-center max-w-2xl mx-auto mb-12">{t("detail.highlightsSub").replace("{neighborhood}", listing.neighborhood)}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listing.features.map((f, i) => (
                <div key={i} className="group p-6 rounded-xl border border-border hover:border-accent/30 transition-colors bg-background shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent transition-colors">
                    <Home className="w-6 h-6 text-accent group-hover:text-accent-foreground transition-colors" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{f}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Categorized Amenities ── */}
      {amenityCategories.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">{t("detail.fullDetails")}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {amenityCategories.map((cat) => {
                const Icon = categoryIcons[cat] || Home;
                const items: string[] = l[cat] || [];
                return (
                  <div key={cat} className="bg-card rounded-xl p-6 border border-border shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-display text-lg font-bold text-foreground">{CATEGORIES[cat].label}</h3>
                    </div>
                    <ul className="space-y-2">
                      {items.map((item) => (
                        <li key={item} className="flex items-center gap-2 font-body text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Location ── */}
      <section className="py-16 bg-background" id="localizacao">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">{t("detail.location")}</h2>
          <p className="font-body text-muted-foreground text-center max-w-2xl mx-auto mb-10">{listing.address ? `${listing.address} — ` : ""}{listing.neighborhood}, Rio de Janeiro - RJ</p>
          <div className="rounded-xl overflow-hidden border border-border shadow-lg">
            <iframe title={`${t("detail.location")} ${listing.title}`} width="100%" height="400" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} className="w-full h-[400px]" />
          </div>
        </div>
      </section>

      {/* ── Simulador de financiamento ── */}
      <FinancingSimulator propertyPrice={listing.price} />

      {/* ── Formulário completo de preferências ── */}
      <section className="py-14 bg-muted" id="preferencias">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground text-center uppercase tracking-tight">
              Não encontrou o que procura?
            </h2>
            <p className="font-display text-lg sm:text-xl md:text-2xl font-semibold text-foreground text-center mt-3">
              Nós encontramos o imóvel do jeito que você procura!
            </p>
            <div className="font-body text-sm sm:text-base text-muted-foreground leading-relaxed space-y-3 mt-4">
              <p>
                Preencha este formulário para receber somente sugestões de imóveis de acordo com
                suas preferências, necessidades e faixa de investimento.
              </p>
              <p className="font-bold text-foreground">
                Fique tranquilo, não fazemos Spam e respeitamos a sua privacidade.
              </p>
            </div>
            <LeadPreferencesForm
              thankYouPath={THANK_YOU.imoveis}
              source={typeof window !== "undefined" ? window.location.href : `/${listing.slug}`}
              defaultNeighborhood={listing.neighborhood}
            />
          </div>
        </div>
      </section>



      {/* ── Corretor Responsável ── */}
      {(l.broker_name || l.broker_phone || l.broker_creci) && (
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">{t("detail.brokerTitle")}</h2>
            <div className="max-w-md mx-auto bg-muted rounded-xl p-8 flex flex-col items-center text-center shadow-lg border border-border">
              {l.broker_photo && (
                <img src={l.broker_photo} alt={l.broker_name || "Corretor"} className="w-28 h-28 rounded-full object-cover border-4 border-accent mb-4" />
              )}
              {l.broker_name && <h3 className="font-display text-xl font-bold text-foreground">{l.broker_name}</h3>}
              {l.broker_creci && <p className="font-body text-sm text-muted-foreground mt-1">CRECI: {l.broker_creci}</p>}
              {l.broker_phone && (
                <a href={`https://wa.me/${l.broker_phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="mt-4">
                  <Button variant="gold"><Phone className="w-4 h-4" /> {l.broker_phone}</Button>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Agende Sua Visita ── */}
      <section className="py-16 bg-secondary" id="agendar">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-foreground mb-3 text-center">{t("detail.scheduleTitle")}</h2>
            <p className="font-body text-secondary-foreground/70 text-center mb-10">{t("detail.scheduleSub")}</p>
            <ScheduleVisitForm
              propertyName={listing.title}
              neighborhood={listing.neighborhood}
              onDark
              submitLabel={t("detail.submitSchedule")}
            />
          </div>
        </div>
      </section>

      {/* ── Fase 4 · Lead Magnet ── */}
      {l.lead_magnet?.enabled && (l.lead_magnet?.title || l.lead_magnet?.file_url) && (
        <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-accent/10">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid md:grid-cols-2 gap-8 items-center bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
              {l.lead_magnet.cover_image && (
                <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-muted">
                  <img
                    src={l.lead_magnet.cover_image}
                    alt={l.lead_magnet.title || "Material exclusivo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-8 md:p-10">
                <div className="inline-block px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-body font-semibold mb-3">
                  🎁 Material exclusivo
                </div>
                {l.lead_magnet.title && (
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
                    {l.lead_magnet.title}
                  </h2>
                )}
                {l.lead_magnet.description && (
                  <p className="font-body text-muted-foreground mb-6 whitespace-pre-line">
                    {l.lead_magnet.description}
                  </p>
                )}
                {l.lead_magnet.file_url ? (
                  <a
                    href={l.lead_magnet.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (window as any).gtag?.("event", "lead_magnet_download", {
                        listing_id: listing.id,
                        listing_title: listing.title,
                      });
                    }}
                  >
                    <Button variant="gold" size="lg">
                      {l.lead_magnet.cta_label || "Baixar material"}
                    </Button>
                  </a>
                ) : (
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="gold" size="lg">
                      {l.lead_magnet.cta_label || "Solicitar material"}
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Fase 4 · Modo Consultor (só para corretores via ?consultor=1) ── */}
      {consultantMode && l.consultant_mode?.enabled && (
        <section className="py-16 bg-secondary" id="consultor">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-body font-bold uppercase tracking-wider mb-3">
                🧠 Modo Consultor
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-foreground mb-2">
                Materiais para o corretor
              </h2>
              <p className="font-body text-secondary-foreground/70">
                Copie e envie diretamente. Estes textos não aparecem para o cliente final.
              </p>
            </div>
            <div className="space-y-4">
              {([
                ["Dossiê do imóvel", l.consultant_mode.dossier],
                ["Pitch de venda", l.consultant_mode.sales_pitch],
                ["Roteiro WhatsApp", l.consultant_mode.whatsapp_script],
                ["Post redes sociais", l.consultant_mode.social_post],
                ["Template de e-mail", l.consultant_mode.email_template],
              ] as [string, string][])
                .filter(([, v]) => v && v.trim())
                .map(([label, val]) => (
                  <div key={label} className="bg-secondary-foreground/5 border border-secondary-foreground/10 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display font-bold text-secondary-foreground">{label}</h3>
                      <Button
                        variant="gold"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(val);
                        }}
                      >
                        Copiar
                      </Button>
                    </div>
                    <pre className="font-body text-sm text-secondary-foreground/80 whitespace-pre-wrap break-words">{val}</pre>
                  </div>
                ))}
              {l.google_ads && (l.google_ads.headline1 || l.google_ads.description1) && (
                <div className="bg-secondary-foreground/5 border border-secondary-foreground/10 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold text-secondary-foreground">Google Ads — headlines & descrições</h3>
                    <Button
                      variant="gold"
                      size="sm"
                      onClick={() => {
                        const ga = l.google_ads;
                        const text = [
                          ga.headline1, ga.headline2, ga.headline3,
                          "",
                          ga.description1, ga.description2,
                          "",
                          ga.offer_text,
                        ].filter(Boolean).join("\n");
                        navigator.clipboard.writeText(text);
                      }}
                    >
                      Copiar tudo
                    </Button>
                  </div>
                  <ul className="font-body text-sm text-secondary-foreground/80 space-y-1">
                    {[l.google_ads.headline1, l.google_ads.headline2, l.google_ads.headline3]
                      .filter(Boolean)
                      .map((h: string, i: number) => (<li key={i}><strong>H{i + 1}:</strong> {h}</li>))}
                    {l.google_ads.description1 && <li><strong>D1:</strong> {l.google_ads.description1}</li>}
                    {l.google_ads.description2 && <li><strong>D2:</strong> {l.google_ads.description2}</li>}
                    {l.google_ads.offer_text && <li><strong>Oferta:</strong> {l.google_ads.offer_text}</li>}
                  </ul>
                  {(l.google_ads.utm_campaign || l.google_ads.utm_source) && (
                    <p className="mt-3 text-xs font-body text-secondary-foreground/50 break-all">
                      URL com UTM: {(() => {
                        const base = l.google_ads.final_url || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
                        const params = new URLSearchParams();
                        ["utm_source","utm_medium","utm_campaign","utm_term","utm_content"].forEach((k) => {
                          const v = l.google_ads[k];
                          if (v) params.set(k, v);
                        });
                        const qs = params.toString();
                        return qs ? `${base}${base.includes("?") ? "&" : "?"}${qs}` : base;
                      })()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Não encontrou seu {type} ideal? ── */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t("detail.notFound").replace("{type}", propertyTypeLabel)}
            </h2>
            <p className="font-body text-muted-foreground mb-8">
              {t("detail.notFoundSub")}
            </p>
            {leadSubmitted ? (
              <div className="py-12 bg-muted rounded-xl border border-border">
                <div className="text-5xl mb-4">🏠</div>
                <h3 className="font-display text-2xl font-bold text-foreground mb-2">{t("detail.requestReceived")}</h3>
                <p className="font-body text-muted-foreground">{t("detail.personalizedOptions")}</p>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit} className="space-y-4 max-w-md mx-auto">
                <Input placeholder={t("detail.yourName")} value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} required className="font-body" />
                <Input type="tel" placeholder={t("detail.whatsappRequired")} value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} required className="font-body" />
                <Input type="email" placeholder={t("detail.emailOptional")} value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className="font-body" />
                <Button type="submit" variant="gold" size="lg" className="w-full">
                  {t("detail.helpFind").replace("{type}", propertyTypeLabel)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── 3 Similar / Related ── */}
      {related.length > 0 && (
        <section className="container mx-auto px-4 py-16">
          <h2 className="font-display text-2xl font-bold text-foreground mb-2 text-center">
            {t("detail.similarIn").replace("{type}", propertyTypeLabel).replace("{neighborhood}", listing.neighborhood)}
          </h2>
          <p className="font-body text-muted-foreground text-center mb-8">{t("detail.seeOther")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => {
              const img = r.images?.[0] || "/placeholder.svg";
              return (
                <Link key={r.id} to={listingPath(r)} className="group bg-card rounded-lg overflow-hidden shadow-lg border border-border hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={optimizedImage(img, 600)} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    {r.listing_code && <span className="text-xs font-semibold text-primary font-body mb-1 block">{t("detail.code")}: {r.listing_code}</span>}
                    <h3 className="font-display font-bold text-foreground line-clamp-1 mb-1">{r.title}</h3>
                    <p className="text-sm text-muted-foreground font-body flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" />{r.neighborhood}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground font-body mb-2">
                      <span className="flex items-center gap-1"><Bed className="h-3 w-3" />{r.bedrooms}</span>
                      <span className="flex items-center gap-1"><Car className="h-3 w-3" />{r.garage_spots || 0}</span>
                      <span className="flex items-center gap-1"><Maximize className="h-3 w-3" />{r.area_m2}m²</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <p className="font-display font-bold text-primary">{formatPrice(r.price, language)}</p>
                      {r.price_per_m2 && <p className="text-xs text-muted-foreground font-body">{formatPrice(r.price_per_m2, language)}/m²</p>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <FaqSection
        scriptId={`listing-${listing.id}`}
        items={[
          ...((Array.isArray(l.faqs) ? l.faqs : []) as any[])
            .filter((f) => f?.question && f?.answer)
            .map((f) => ({ question: f.question, answer: f.answer })),
          {
            question: `Qual o valor do ${propertyTypeLabel} ${listing.title}?`,
            answer: `${listing.title} está anunciado por ${formatPrice(listing.price, language)}${l.condo_fee ? `, com condomínio de ${formatPrice(l.condo_fee, language)}` : ""}${l.iptu ? ` e IPTU de ${formatPrice(l.iptu, language)}` : ""}.`,
          },
          {
            question: `Quantos quartos e vagas tem este ${propertyTypeLabel}?`,
            answer: `São ${listing.bedrooms} quartos${listing.suites ? ` (${listing.suites} suíte${listing.suites > 1 ? "s" : ""})` : ""}, ${listing.bathrooms || 1} banheiro(s) e ${listing.garage_spots || 0} vaga(s) de garagem, com ${listing.area_m2}m² de área.`,
          },
          {
            question: `Onde fica localizado?`,
            answer: `Este imóvel está localizado ${listing.address ? `em ${listing.address}, ` : ""}no bairro ${listing.neighborhood}, Rio de Janeiro — RJ.`,
          },
          {
            question: `Como agendar uma visita?`,
            answer: `Você pode agendar uma visita imediatamente pelo WhatsApp (21) 97354-8387 ou preencher o formulário nesta página. Nossos corretores respondem em minutos.`,
          },
          {
            question: `O imóvel aceita financiamento?`,
            answer: `Sim. Trabalhamos com as principais linhas de financiamento imobiliário e podemos indicar bancos parceiros para simulação e aprovação de crédito.`,
          },
        ]}
      />

      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
};

export default ListingDetail;
